import Cookies from "js-cookie";

export interface BuilderTestData {
    testId: string;
    variantId: string;
}

export class BuilderTestUtils {
    /**
     * Extracts all Builder.io test cookies and returns them as an array
     * Cookie format: builder.tests.{contentId}_{experimentId}={variantId}
     */
    static getBuilderTests(): BuilderTestData[] {
        const allCookies = Cookies.get();
        const tests: BuilderTestData[] = [];

        for (const cookieName in allCookies) {
            if (cookieName.startsWith("builder.tests.")) {
                const testId = cookieName.replace("builder.tests.", "");
                const variantId = allCookies[cookieName];

                if (testId && variantId) {
                    tests.push({ testId, variantId });
                }
            }
        }

        return tests;
    }

    /**
     * Formats Builder test data as query parameters for DataDog filtering
     * Format: builderVariant.testId1=variantId1&builderVariant.testId2=variantId2
     * Returns a Record for easy spreading into URLSearchParams or tracking params
     */
    static formatBuilderTestsAsParams(tests: BuilderTestData[]): Record<string, string> {
        const params: Record<string, string> = {};

        for (const test of tests) {
            const paramKey = `builderVariant.${test.testId}`;
            params[paramKey] = test.variantId;
        }

        return params;
    }

    /**
     * Gets Builder test data and returns it as a params object
     * Returns empty object if no tests found
     */
    static getBuilderTestParams(): Record<string, string> {
        const tests = BuilderTestUtils.getBuilderTests();
        return BuilderTestUtils.formatBuilderTestsAsParams(tests);
    }

    /**
     * Server-side version that extracts Builder tests from cookie string
     * Used in middleware where we don't have access to js-cookie
     */
    static getBuilderTestsFromCookieString(cookieString: string): BuilderTestData[] {
        const tests: BuilderTestData[] = [];

        if (!cookieString) return tests;

        const cookies = cookieString.split(";").reduce((acc, cookie) => {
            const [name, value] = cookie
                .trim()
                .split("=")
                .map(c => {
                    try {
                        return decodeURIComponent(c);
                    } catch {
                        return c;
                    }
                });
            if (name) acc[name] = value || "";
            return acc;
        }, {} as Record<string, string>);

        for (const cookieName in cookies) {
            if (cookieName.startsWith("builder.tests.")) {
                const testId = cookieName.replace("builder.tests.", "");
                const variantId = cookies[cookieName];

                if (testId && variantId) {
                    tests.push({ testId, variantId });
                }
            }
        }

        return tests;
    }

    /**
     * Parses Builder test query params back into structured data
     * Looks for all query params that start with "builderVariant."
     */
    static parseBuilderTestQueryParams(queryParams: Record<string, string>): BuilderTestData[] {
        const tests: BuilderTestData[] = [];

        for (const [key, value] of Object.entries(queryParams)) {
            if (key.startsWith("builderVariant.")) {
                const testId = key.replace("builderVariant.", "");
                tests.push({ testId, variantId: value });
            }
        }

        return tests;
    }
}
